export const PAGE_SIZE = 50;
export const LINES_PAGE_SIZE = 50;
export const VARIANCE_PAGE_SIZE = 200;
export const MOVEMENT_PAGE_SIZE = 100;
export const BATCH_SEARCH_LIMIT = 50;
export const PRODUCT_SEARCH_LIMIT = 50;
export const COUNT_SAVE_CHUNK = 500;
export const MAX_COUNT_QTY = 1_000_000;

export const LOSS_CATEGORIES = ['expired', 'damaged', 'theft', 'other'] as const;
export type LossCategory = (typeof LOSS_CATEGORIES)[number];

export const BATCH_STATUSES = ['active', 'quarantined', 'recalled'] as const;
export type BatchStatus = (typeof BATCH_STATUSES)[number];

export const ADJUSTMENT_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;

export interface StoreOption {
  id: string;
  name: string;
  branch: { id: string; name: string } | null;
}

export interface ProductHit {
  id: string;
  code: string;
  genericName: string;
  brandName: string | null;
  strength: string | null;
}

export interface StockBatchHit {
  id: string;
  product: {
    id: string;
    code: string;
    genericName: string;
    brandName: string | null;
    strength: string | null;
    unit: string;
    isControlled: boolean;
  };
  store: { id: string; name: string };
  batchNo: string;
  expiryDate: string;
  daysToExpiry: number;
  qtyOnHand: number;
  purchasePriceMinor: number;
  salePriceMinor: number;
  status: string;
  statusReason: string | null;
}

export interface StockSearchResult {
  data: StockBatchHit[];
  nextCursor: string | null;
}

export interface AdjustmentOutcome {
  status: 'applied' | 'pending_approval';
  adjusted: number;
  valueMinor: number;
  netValueMinor: number;
  requestId?: string;
  number?: string;
}

export interface AdjustmentRow {
  id: string;
  number: string;
  type: string;
  source: string;
  status: string;
  lineCount: number;
  valueMinor: number;
  netValueMinor: number;
  note: string | null;
  requestedAt: string;
  requestedBy: string | null;
  requestedByName: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  store: { id: string; name: string };
  stockTakeId: string | null;
}

export interface AdjustmentLine {
  stockBatchId: string;
  qtyDelta: number;
  reason: string;
  lossCategory: string | null;
  batchNo: string;
  productId: string;
  productName: string;
  unitCostMinor: number;
}

export interface AdjustmentDetail extends AdjustmentRow {
  lines: AdjustmentLine[];
}

export type AdjustmentDecision = 'approve' | 'reject' | 'withdraw';

export interface StockTakeSummary {
  id: string;
  number: string;
  status: string;
  blind: boolean;
  snapshotAt: string;
  scopeNote: string | null;
  notes: string | null;
  lineCount: number;
  varianceValueMinor: number | null;
  createdAt: string;
  closedAt: string | null;
  store: { id: string; name: string };
}

export interface StockTakeDetail extends StockTakeSummary {
  progress: { pending: number; counted: number; disputed?: number };
  adjustmentRequests: { id: string; number: string; status: string }[];
}

export interface CountEntry {
  userId: string;
  userName: string;
  qty: number;
  at: string;
}

export interface StockTakeLine {
  id: string;
  stockBatchId: string;
  product: { code: string; genericName: string; strength: string | null; unit: string };
  batchNo: string;
  expiryDate: string;
  systemQty: number | null;
  countedQty: number | null;
  status: string;
  counts: CountEntry[];
  resolutionNote: string | null;
}

export interface StockTakeLines {
  blind: boolean;
  status: string;
  lines: StockTakeLine[];
}

export interface CountSaveResult {
  saved: number;
  disputed?: number;
}

export interface VarianceSummary {
  shortageMinor: number;
  overageMinor: number;
  netMinor: number;
  shortageLines: number;
  overageLines: number;
  matchedLines: number;
  uncountedLines: number;
}

export interface VarianceLine {
  lineId: string;
  stockBatchId: string;
  code: string;
  name: string;
  strength: string | null;
  unit: string;
  batchNo: string;
  expiryDate: string;
  status: string;
  systemQty: number;
  movedSinceSnapshotQty: number;
  expectedQty: number | null;
  countedQty: number | null;
  varianceQty: number | null;
  unitCostMinor: number;
  varianceValueMinor: number | null;
}

export interface StockTakeVariance {
  number: string;
  status: string;
  snapshotAt: string;
  summary: VarianceSummary;
  totalLines: number;
  lines: VarianceLine[];
}

export interface CloseSheetResult {
  number: string;
  summary: VarianceSummary;
  adjustment: AdjustmentOutcome | null;
}

export interface BlockedBatch {
  id: string;
  product: { code: string; genericName: string; strength: string | null };
  store: { id: string; name: string };
  batchNo: string;
  expiryDate: string;
  qtyOnHand: number;
  status: string;
  statusReason: string | null;
  statusChangedAt: string | null;
  valueMinor: number;
}

export interface BatchStatusResult {
  batches: number;
  changed: number;
  status: string;
}

export interface RecallBatch {
  id: string;
  store: { id: string; name: string };
  expiryDate: string;
  qtyOnHand: number;
  status: string;
  statusReason: string | null;
}

export interface RecallReceipt {
  grnId: string;
  grnNumber: string;
  receivedAt: string;
  supplierInvoiceNo: string | null;
  supplier: { id: string; name: string; phone: string | null };
  qty: number;
}

export interface RecallTotal {
  type: string;
  movements: number;
  qty: number;
}

export interface RecallMovement {
  at: string;
  type: string;
  qty: number;
  store: string;
  reason: string | null;
  documentType: string | null;
  documentNo: string | null;
  counterparty: string | null;
  contact: string | null;
}

export interface RecallReport {
  product: { code: string; genericName: string; strength: string | null; unit: string };
  batchNo: string;
  batches: RecallBatch[];
  receivedFrom: RecallReceipt[];
  totalsByType: RecallTotal[];
  movements: RecallMovement[];
}

export interface StockLimitSettings {
  supplierPaymentApprovalLimitMinor?: number | null;
  stockAdjustApprovalLimitMinor?: number | null;
  posReturnApprovalLimitMinor?: number | null;
}
